import { useEffect, useMemo, useRef, useState } from 'react'
import Map, { Source, Layer, type MapRef } from 'react-map-gl/maplibre'
import { Box, Text } from '@chakra-ui/react';
import 'maplibre-gl/dist/maplibre-gl.css'
import type { LngLatBoundsLike } from 'maplibre-gl'
import { getMapStyle, DEFAULT_VIEW_STATE } from '../../lib/mapConfig'

// Dark→bright "city lights" ramp keyed to heatmap density.
const HEATMAP_COLOR = [
  'interpolate', ['linear'], ['heatmap-density'],
  0, 'rgba(10,14,20,0)',
  0.2, 'rgba(15,53,49,0.6)',
  0.5, 'rgba(20,120,110,0.85)',
  0.8, 'rgba(79,209,197,0.95)',
  1, 'rgba(220,245,240,1)',
] as unknown[]

interface Props {
  points: GeoJSON.FeatureCollection | null
  boundary?: GeoJSON.FeatureCollection | null
  // Widened from the brief's literal `[[number, number], [number, number]]` to maplibre's actual
  // LngLatBoundsLike: featureCollectionBounds() (choropleth.ts) returns LngLatBoundsLike, which
  // includes a flat [minLng, minLat, maxLng, maxLat] tuple form, not just the nested-pair form.
  fitBounds?: LngLatBoundsLike | null
  caption?: string
  height?: string
}

export function DensityHeatmap({ points, boundary = null, fitBounds = null, caption, height = '300px' }: Props) {
  const mapRef = useRef<MapRef>(null)
  const [mapLoaded, setMapLoaded] = useState(false)

  // Scale the heatmap-weight ramp to the actual data max so the brightest precinct always saturates,
  // rather than a static ceiling that leaves sparse universes uniformly dark. Floor at 1 to avoid /0.
  const maxWeight = useMemo(
    () => Math.max(1, ...(points?.features.map(f => Number(f.properties?.weight) || 0) ?? [])),
    [points],
  )

  // Fit to the boundary once the map is loaded AND the fit bounds exist — either
  // may arrive first (map style load vs. async boundary fetch), so gate on both.
  useEffect(() => {
    if (!fitBounds || !mapLoaded || !mapRef.current) return
    mapRef.current.fitBounds(fitBounds, { padding: 40, maxZoom: 13, duration: 0 })
  }, [fitBounds, mapLoaded])

  return (
    <Box>
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden" h={height} bg="#0a0e14">
        <Map
          ref={mapRef}
          initialViewState={DEFAULT_VIEW_STATE}
          style={{ width: '100%', height: '100%' }}
          mapStyle={getMapStyle()}
          onLoad={() => setMapLoaded(true)}
        >
          {boundary && (
            <Source id="density-boundary" type="geojson" data={boundary}>
              <Layer id="density-boundary-line" type="line"
                paint={{ 'line-color': '#AEB9D6', 'line-width': 1.5, 'line-dasharray': [3, 2] }} />
            </Source>
          )}
          {points && (
            <Source id="density-points" type="geojson" data={points}>
              <Layer id="density-heat" type="heatmap"
                paint={{
                  'heatmap-weight': ['interpolate', ['linear'], ['get', 'weight'], 0, 0, maxWeight, 1] as unknown as never,
                  'heatmap-intensity': 1,
                  'heatmap-color': HEATMAP_COLOR as never,
                  'heatmap-radius': 18,
                  'heatmap-opacity': 0.9,
                }} />
            </Source>
          )}
        </Map>
      </Box>
      {caption && (
        <Text fontSize="xs" color="gray.500" mt={2}>{caption}</Text>
      )}
    </Box>
  )
}
